/**
 * LogoutConfirmModal — native in-app "are you sure you want to sign out?" prompt.
 *
 * Only ever renders while the AuthContext logout modal is open, which only
 * happens in native mode (nativeAuthEnabled). It replaces Keycloak's hosted
 * "Do you want to log out?" confirmation page: confirming ends the Keycloak SSO
 * session back-channel (no redirect). Inherits partner branding via --partner-*.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export const LogoutConfirmModal: React.FC = () => {
  const { t } = useTranslation('auth');
  const { logoutModalOpen, confirmLogout, cancelLogout } = useAuth();

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (logoutModalOpen) {
      setMounted(true);
      setBusy(false);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    const timer = setTimeout(() => setMounted(false), 200);
    return () => clearTimeout(timer);
  }, [logoutModalOpen]);

  useEffect(() => {
    if (!logoutModalOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cancelLogout(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [logoutModalOpen, cancelLogout]);

  if (!mounted) return null;

  const handleConfirm = async () => {
    setBusy(true);
    await confirmLogout();
  };

  const primaryBtnStyle: React.CSSProperties = {
    background: 'var(--partner-button-bg, #F5A623)',
    color: 'var(--partner-button-fg, #14233a)',
    border: 'none',
  };

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="logout-modal-title"
      onMouseDown={cancelLogout}
      style={{
        position: 'fixed', inset: 0, zIndex: 1085, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
        background: 'rgba(0,0,0,0.55)', opacity: visible ? 1 : 0, transition: 'opacity 200ms ease',
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 380, background: '#fff', borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden',
          transform: visible ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.98)',
          transition: 'transform 200ms ease',
        }}
      >
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px',
            background: 'var(--partner-modal-header-bg, #14233a)', color: 'var(--partner-modal-header-fg, #ffffff)',
          }}
        >
          <span id="logout-modal-title" className="d-flex align-items-center gap-2 fw-semibold">
            <LogOut size={18} /> {t('logout.title')}
          </span>
          <button
            type="button" aria-label={t('close')} onClick={cancelLogout}
            className="btn btn-sm p-1 d-flex"
            style={{ color: 'inherit', background: 'transparent', border: 'none', lineHeight: 1 }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          <p className="mb-4 text-center">{t('logout.confirm')}</p>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-outline-secondary flex-fill" onClick={cancelLogout} disabled={busy}>
              {t('cancel')}
            </button>
            <button type="button" className="btn fw-semibold flex-fill" style={primaryBtnStyle} onClick={handleConfirm} disabled={busy}>
              {busy ? t('logout.busy') : t('logout.title')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
